import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  FileText, Download, FilePdf, FileXls, Receipt, CircleNotch,
  ArrowSquareOut, Users, Truck, ArrowsClockwise,
} from '@phosphor-icons/react';

import { ChartCard, TrendBarChart, DonutChart } from '../../components/reports/FinanceCharts';
import { DataNote } from '../../components/reports/DataNote';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGING_CHART_COLORS = { Current: '#059669', '1-30': '#d97706', '31-60': '#ea580c', '61-90': '#dc2626', '90+': '#9f1239' };
const agingData = (aging) => Object.entries(aging || {})
  .filter(([k]) => AGING_CHART_COLORS[k])
  .map(([k, v]) => ({ name: k === 'Current' ? 'Current (not due)' : `${k} days`, value: v, color: AGING_CHART_COLORS[k] }));

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AGING_COLORS = {
  Current: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  '1-30':  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  '31-60': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  '61-90': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  '90+':   'bg-rose-200 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200',
  Settled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

export default function StatementOfAccountsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(searchParams.get('tab') || 'customer');
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [partyId, setPartyId] = useState(searchParams.get('id') || '');
  const [fromDate, setFromDate] = useState(searchParams.get('from') || '');
  const [toDate, setToDate]   = useState(searchParams.get('to')   || '');

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const partyList = tab === 'customer' ? customers : suppliers;
  const partyKind = tab === 'customer' ? 'customer' : 'supplier';

  /* -------- bootstrap contacts -------- */
  const fetchContacts = useCallback(async () => {
    try {
      const [{ data: c }, { data: s }] = await Promise.all([
        axios.get(`${API_URL}/contacts?contact_type=customer`),
        axios.get(`${API_URL}/contacts?contact_type=supplier`),
      ]);
      setCustomers(c || []);
      setSuppliers(s || []);
    } catch (e) {
      toast.error('Failed to load contacts');
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  /* -------- when tab/party/date changes, sync URL & load -------- */
  useEffect(() => {
    const sp = new URLSearchParams();
    sp.set('tab', tab);
    if (partyId) sp.set('id', partyId);
    if (fromDate) sp.set('from', fromDate);
    if (toDate) sp.set('to', toDate);
    setSearchParams(sp, { replace: true });
  }, [tab, partyId, fromDate, toDate, setSearchParams]);

  const loadStatement = useCallback(async () => {
    if (!partyId) { setStatement(null); return; }
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const { data } = await axios.get(
        `${API_URL}/reports/soa/${partyKind}/${partyId}`,
        { params },
      );
      setStatement(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load statement');
      setStatement(null);
    } finally {
      setLoading(false);
    }
  }, [partyId, partyKind, fromDate, toDate]);

  useEffect(() => { loadStatement(); }, [loadStatement]);

  /* -------- summary loader -------- */
  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const params = {};
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const path = tab === 'customer' ? 'customers' : 'suppliers';
      const { data } = await axios.get(
        `${API_URL}/reports/soa/${path}/summary`,
        { params },
      );
      setSummary(data);
    } catch (e) {
      toast.error('Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [tab, fromDate, toDate]);

  useEffect(() => {
    if (!partyId) loadSummary();
  }, [partyId, loadSummary]);

  /* -------- download helpers -------- */
  const downloadFile = async (format) => {
    if (!partyId) {
      toast.error('Please choose a party first');
      return;
    }
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      const url = `${API_URL}/reports/soa/${partyKind}/${partyId}/${format}?${params.toString()}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const partyName =
        (statement?.party?.company_name || statement?.party?.name || partyKind).replace(/\s+/g, '_');
      const ext = format === 'excel' ? 'xlsx' : format;
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `SoA_${partyName}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const previewPdf = async () => {
    if (!partyId) return;
    const params = new URLSearchParams();
    if (fromDate) params.append('from', fromDate);
    if (toDate) params.append('to', toDate);
    const token = localStorage.getItem('erp_token');
    try {
      const res = await axios.get(
        `${API_URL}/reports/soa/${partyKind}/${partyId}/pdf?${params.toString()}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  /* -------- derived totals -------- */
  const totals = useMemo(() => {
    if (!statement) return null;
    const s = statement.summary || {};
    return {
      invoiced: s.total_invoiced_in_period ?? s.total_billed_in_period ?? 0,
      tax: s.total_tax_in_period ?? 0,
      paid: s.total_received_in_period ?? s.total_paid_in_period ?? 0,
      outstanding: s.total_outstanding ?? 0,
      aging: s.aging || {},
    };
  }, [statement]);

  const closingLabel = tab === 'customer' ? 'Balance Receivable' : 'Balance Payable';
  const inviceLabel = tab === 'customer' ? 'Invoiced' : 'Billed';
  const paidLabel = tab === 'customer' ? 'Received' : 'Paid';

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Statement of Accounts</h1>
          <p className="text-sm text-muted-foreground">
            Receivables &amp; Payables — per client, per date range, with aging.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { loadStatement(); loadSummary(); }} data-testid="soa-refresh-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <DataNote />

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setPartyId(''); setStatement(null); }}>
        <TabsList>
          <TabsTrigger value="customer" data-testid="soa-tab-customer">
            <Users size={14} className="mr-2" /> Receivables (Customers)
          </TabsTrigger>
          <TabsTrigger value="supplier" data-testid="soa-tab-supplier">
            <Truck size={14} className="mr-2" /> Payables (Suppliers)
          </TabsTrigger>
        </TabsList>

        {/* ----- Filters card ----- */}
        <div className="mt-4 rounded-lg border bg-card p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-5 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {tab === 'customer' ? 'Customer' : 'Supplier'}
            </Label>
            <Select value={partyId} onValueChange={setPartyId}>
              <SelectTrigger data-testid="soa-party-select">
                <SelectValue placeholder={`Choose a ${tab}…`} />
              </SelectTrigger>
              <SelectContent>
                {partyList.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.company_name || p.name}{p.company_name && p.name ? ` · ${p.name}` : ''}
                  </SelectItem>
                ))}
                {partyList.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">No {tab}s found.</div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="soa-from" />
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="soa-to" />
          </div>
          <div className="md:col-span-3 flex items-center gap-2 justify-end flex-wrap">
            <Button variant="outline" size="sm" onClick={previewPdf} disabled={!partyId || loading} data-testid="soa-preview-pdf">
              <FilePdf size={14} className="mr-1" /> Preview
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadFile('pdf')} disabled={!partyId || loading} data-testid="soa-download-pdf">
              <Download size={14} className="mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadFile('excel')} disabled={!partyId || loading} data-testid="soa-download-xlsx">
              <FileXls size={14} className="mr-1" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadFile('csv')} disabled={!partyId || loading} data-testid="soa-download-csv">
              <FileText size={14} className="mr-1" /> CSV
            </Button>
          </div>
        </div>

        <TabsContent value="customer" className="mt-4" />
        <TabsContent value="supplier" className="mt-4" />
      </Tabs>

      {/* ---------- Body ---------- */}
      {!partyId && (
        <SummaryCard
          summary={summary}
          loading={summaryLoading}
          kind={tab}
          onPick={(id) => setPartyId(id)}
        />
      )}

      {partyId && loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <CircleNotch size={20} className="animate-spin mr-2" /> Building statement…
        </div>
      )}

      {partyId && !loading && statement && (
        <>
          {/* KPI bar */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard label={`Opening Balance`} value={statement.opening_balance} subtle />
            <KpiCard label={`Total ${inviceLabel}`} value={totals.invoiced} />
            <KpiCard label={`Tax in Period`} value={totals.tax} />
            <KpiCard label={`Total ${paidLabel}`} value={totals.paid} />
            <KpiCard label={closingLabel} value={statement.closing_balance} highlight />
          </div>

          <PartyCharts statement={statement} aging={totals.aging} isCust={tab === 'customer'} />

          {/* Aging bar */}
          <div className="rounded-lg border bg-card p-4">
            <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">
              Aging Buckets (all-time outstanding)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.entries(totals.aging).map(([k, v]) => (
                <div key={k} className="rounded-md border bg-background p-3 flex flex-col">
                  <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded inline-block w-fit ${AGING_COLORS[k] || ''}`}>{k}</span>
                  <span className="mt-1 font-mono text-lg font-semibold">{fmtMoney(v)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Party info strip */}
          <div className="rounded-lg border bg-muted/30 p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
            <span><strong>{tab === 'customer' ? 'Customer' : 'Supplier'}:</strong> {statement.party.company_name || statement.party.name}</span>
            {statement.party.tax_id && <span><strong>TRN:</strong> {statement.party.tax_id}</span>}
            {statement.party.email && <span><strong>Email:</strong> {statement.party.email}</span>}
            {statement.party.phone && <span><strong>Phone:</strong> {statement.party.phone}</span>}
          </div>

          {/* Ledger table */}
          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Due</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Doc #</th>
                    <th className="px-3 py-2 text-left">Project</th>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-right">Tax</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Paid</th>
                    <th className="px-3 py-2 text-right">Payable</th>
                    <th className="px-3 py-2 text-right">Debit</th>
                    <th className="px-3 py-2 text-right">Credit</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2 text-center">Days OD</th>
                    <th className="px-3 py-2 text-left">Aging</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-muted/20 border-y border-border">
                    <td colSpan={5} className="px-3 py-2 font-semibold">Opening Balance</td>
                    <td colSpan={7} />
                    <td className="px-3 py-2 text-right font-semibold font-mono">{fmtMoney(statement.opening_balance)}</td>
                    <td colSpan={2} />
                  </tr>
                  {statement.rows.length === 0 && (
                    <tr><td colSpan={15} className="px-3 py-8 text-center text-muted-foreground">No transactions in this period.</td></tr>
                  )}
                  {statement.rows.map((r) => (
                    <tr key={`${r.doc_type}-${r.id}`} className="border-b border-border last:border-b-0 hover:bg-muted/20" data-testid={`soa-row-${r.id}`}>
                      <td className="px-3 py-2">{r.date}</td>
                      <td className="px-3 py-2">{r.due_date || '—'}</td>
                      <td className="px-3 py-2 capitalize">
                        <Badge variant="outline" className="text-[10px]">{r.doc_type}</Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.doc_number}</td>
                      <td className="px-3 py-2 max-w-[160px] truncate">{r.project_reference || '—'}</td>
                      <td className="px-3 py-2 max-w-[320px]">
                        <div className="truncate" title={r.description}>{r.description}</div>
                        {r.contract_note && (
                          <div className="text-[11px] leading-snug text-amber-700 dark:text-amber-400 mt-0.5" data-testid={`soa-contract-note-${r.id}`}>
                            {r.contract_note}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.tax_total)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.total)}</td>
                      <td className="px-3 py-2 text-right font-mono" data-testid={`soa-paid-${r.id}`}>{r.doc_type === 'invoice' ? fmtMoney(r.paid_amount) : ''}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold" data-testid={`soa-payable-${r.id}`}>{r.doc_type === 'invoice' ? fmtMoney(r.payable ?? r.balance) : ''}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">{r.debit > 0 ? fmtMoney(r.debit) : ''}</td>
                      <td className="px-3 py-2 text-right font-mono text-rose-700 dark:text-rose-400">{r.credit > 0 ? fmtMoney(r.credit) : ''}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtMoney(r.running_balance)}</td>
                      <td className="px-3 py-2 text-center">{r.days_overdue || ''}</td>
                      <td className="px-3 py-2">
                        {r.aging_bucket && (
                          <Badge className={`${AGING_COLORS[r.aging_bucket] || ''} text-[10px] font-medium`}>
                            {r.aging_bucket}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50 dark:bg-amber-950/30 border-t-2 border-amber-300 dark:border-amber-800">
                    <td colSpan={12} className="px-3 py-3 font-bold text-base">{closingLabel} (Closing Balance)</td>
                    <td className="px-3 py-3 text-right font-bold font-mono text-base" data-testid="soa-closing-balance">{fmtMoney(statement.closing_balance)}</td>
                    <td colSpan={2} />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* -------- KPI card -------- */
function KpiCard({ label, value, highlight, subtle }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800' : subtle ? 'bg-muted/30' : 'bg-card'}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-mono font-semibold ${highlight ? 'text-xl' : 'text-lg'}`}>{fmtMoney(value)}</div>
    </div>
  );
}

/* -------- All-clients summary -------- */
function SummaryCard({ summary, loading, kind, onPick }) {
  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6 text-center text-muted-foreground">
        <CircleNotch size={20} className="animate-spin inline mr-2" /> Loading {kind === 'customer' ? 'receivables' : 'payables'} summary…
      </div>
    );
  }
  if (!summary) return null;
  const isCust = kind === 'customer';
  const t = summary.totals || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label={isCust ? 'Total Invoiced' : 'Total Billed'} value={t.total_invoiced || t.total_billed || 0} />
        <KpiCard label={isCust ? 'Total Received' : 'Total Paid'} value={t.total_received || t.total_paid || 0} />
        <KpiCard label={`Total ${isCust ? 'Receivable' : 'Payable'}`} value={t.total_balance || 0} highlight />
        <KpiCard label="Current (not due)" value={(t.aging || {}).Current || 0} subtle />
        <KpiCard label="90+ Days" value={(t.aging || {})['90+'] || 0} subtle />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <ChartCard title={`Top ${isCust ? 'customers' : 'suppliers'} by balance`} subtitle="Outstanding amount" className="lg:col-span-2" testid="soa-top-balance-chart">
          <TrendBarChart
            isMonth={false}
            xKey="name"
            data={[...(summary.rows || [])].sort((a, b) => b.balance - a.balance).slice(0, 10).map((r) => ({
              name: (r.customer_name || r.supplier_name || '').slice(0, 14),
              balance: r.balance,
              paid: r.total_received || r.total_paid || 0,
            }))}
            series={[{ key: 'balance', label: 'Balance', color: '#dc2626' }, { key: 'paid', label: isCust ? 'Received' : 'Paid', color: '#059669' }]}
          />
        </ChartCard>
        <ChartCard title="Aging of outstanding" subtitle="Share by days overdue" testid="soa-aging-chart">
          <DonutChart data={agingData(t.aging)} />
        </ChartCard>
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
          <div className="font-semibold">{isCust ? 'Customers' : 'Suppliers'} with Outstanding Balance</div>
          <span className="text-xs text-muted-foreground">{(summary.rows || []).length} row(s) — pick one to open detailed statement</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/20 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">{isCust ? 'Customer' : 'Supplier'}</th>
                <th className="px-3 py-2 text-right">{isCust ? 'Invoiced' : 'Billed'}</th>
                <th className="px-3 py-2 text-right">{isCust ? 'Received' : 'Paid'}</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-right">Current</th>
                <th className="px-3 py-2 text-right">1-30</th>
                <th className="px-3 py-2 text-right">31-60</th>
                <th className="px-3 py-2 text-right">61-90</th>
                <th className="px-3 py-2 text-right">90+</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(summary.rows || []).length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">No outstanding balances.</td></tr>
              )}
              {(summary.rows || []).map((r) => {
                const id = r.customer_id || r.supplier_id;
                const name = r.customer_name || r.supplier_name;
                return (
                  <tr key={id} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                    <td className="px-3 py-2 font-medium">{name}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.total_invoiced || r.total_billed)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.total_received || r.total_paid)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtMoney(r.balance)}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">{fmtMoney(r.aging.Current)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-700 dark:text-amber-400">{fmtMoney(r.aging['1-30'])}</td>
                    <td className="px-3 py-2 text-right font-mono text-orange-700 dark:text-orange-400">{fmtMoney(r.aging['31-60'])}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-700 dark:text-red-400">{fmtMoney(r.aging['61-90'])}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-800 dark:text-rose-300">{fmtMoney(r.aging['90+'])}</td>
                    <td className="px-3 py-2">
                      <Button variant="ghost" size="sm" onClick={() => onPick(id)} data-testid={`soa-open-${id}`}>
                        <ArrowSquareOut size={14} className="mr-1" /> Open
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* -------- Per-party charts -------- */
function PartyCharts({ statement, aging, isCust }) {
  const monthly = useMemo(() => {
    const m = {};
    (statement.rows || []).forEach((r) => {
      const k = (r.date || '').slice(0, 7);
      if (!k) return;
      m[k] = m[k] || { month: k, billed: 0, settled: 0 };
      m[k].billed += isCust ? (r.debit || 0) : (r.credit || 0);
      m[k].settled += isCust ? (r.credit || 0) : (r.debit || 0);
    });
    return Object.values(m).sort((a, b) => a.month.localeCompare(b.month));
  }, [statement, isCust]);
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <ChartCard title={`${isCust ? 'Invoiced vs Received' : 'Billed vs Paid'} by month`} className="lg:col-span-2" testid="soa-party-trend-chart">
        <TrendBarChart
          data={monthly}
          series={[{ key: 'billed', label: isCust ? 'Invoiced' : 'Billed', color: '#2563eb' }, { key: 'settled', label: isCust ? 'Received' : 'Paid', color: '#059669' }]}
        />
      </ChartCard>
      <ChartCard title="Aging of outstanding" testid="soa-party-aging-chart">
        <DonutChart data={agingData(aging)} />
      </ChartCard>
    </div>
  );
}
